import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import AnimateOnScroll from './AnimateOnScroll.js';
import PageHero from './PageHero.js';
import regionOkanagan from '../assets/region-okanagan.jpg';

const OkanaganPage = () => {
  return (
    <div className="bg-custom-earth">
      <div className="wood-container">
        <div className="min-h-screen flex flex-col">
          <Helmet>
            <title>Vegetation Management Okanagan Region | Boultbee</title>
            <meta name="description" content="Herbicide application services in Penticton, Kelowna, Vernon, and the Okanagan. Local team, GPS-tracked treatments, bare ground and noxious weed control." />
            <link rel="canonical" href="https://www.weedmanager.com/service-areas/okanagan" />
            <meta property="og:type" content="website" />
            <meta property="og:site_name" content="Boultbee Vegetation Management" />
            <meta property="og:title" content="Vegetation Management Okanagan Region | Boultbee" />
            <meta property="og:description" content="Herbicide application services in Penticton, Kelowna, Vernon, and the Okanagan. Local team, GPS-tracked treatments, bare ground and noxious weed control." />
            <meta property="og:url" content="https://www.weedmanager.com/service-areas/okanagan" />
            <meta property="og:image" content="https://www.weedmanager.com/images/og-okanagan.jpg" />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content="Vegetation Management Okanagan Region | Boultbee" />
            <meta name="twitter:description" content="Herbicide application services in Penticton, Kelowna, Vernon, and the Okanagan. Local team, GPS-tracked treatments, bare ground and noxious weed control." />
            <meta name="twitter:image" content="https://www.weedmanager.com/images/og-okanagan.jpg" />
            <script type="application/ld+json">
              {JSON.stringify({
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                "itemListElement": [
                  { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.weedmanager.com/" },
                  { "@type": "ListItem", "position": 2, "name": "Okanagan & Similkameen", "item": "https://www.weedmanager.com/service-areas/okanagan" }
                ]
              })}
            </script>
            <script type="application/ld+json">
              {JSON.stringify({
                "@context": "https://schema.org",
                "@type": "Service",
                "serviceType": "Industrial Vegetation Management",
                "provider": {
                  "@type": "LocalBusiness",
                  "name": "Boultbee Vegetation Management",
                  "url": "https://www.weedmanager.com"
                },
                "areaServed": [
                  { "@type": "City", "name": "Penticton" },
                  { "@type": "City", "name": "Kelowna" },
                  { "@type": "City", "name": "Vernon" },
                  { "@type": "City", "name": "West Kelowna" },
                  { "@type": "City", "name": "Summerland" },
                  { "@type": "City", "name": "Oliver" },
                  { "@type": "City", "name": "Osoyoos" },
                  { "@type": "City", "name": "Lake Country" }
                ],
                "description": "Herbicide application services in the Okanagan region including bare ground control, turf management, and noxious weed treatment for industrial, municipal, and utility properties."
              })}
            </script>
          </Helmet>

          <PageHero
            title="Okanagan & Similkameen"
            subtitle="Your local vegetation management team"
            backgroundImage={regionOkanagan}
            bgPosition="center bottom"
          />

          <AnimateOnScroll animation="fade-in-up">
            <p className="text-center text-lg md:text-xl font-serif italic text-green-900 max-w-3xl mx-auto px-6 py-6">Based in Penticton, Boultbee is the Okanagan's most experienced vegetation management provider. We serve clients from Osoyoos to Vernon with local crews who understand the region's unique climate and vegetation challenges.</p>
          </AnimateOnScroll>

          <AnimateOnScroll animation="fade-in-up">
            <div className="text-container-blur">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">Our Home Region</h2>
              <p className="text-gray-700">The Okanagan is where Boultbee began and where our headquarters remain. Our Penticton-based team has unmatched knowledge of the region's semi-arid grassland ecology, unique invasive species pressures, and the specific vegetation challenges across the Okanagan. From Osoyoos and Oliver in the south through Penticton, Summerland, and Kelowna to Vernon and Lake Country in the north, no company knows Okanagan vegetation better.</p>
            </div>
          </AnimateOnScroll>

          <AnimateOnScroll animation="fade-in-up">
            <div className="text-container-blur">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">Okanagan-Specific Services</h2>
              <p className="text-gray-700">The Okanagan's hot, dry summers and mild winters create a distinctive growing environment with year-round vegetation management needs. We provide bare ground control for industrial yards, rail facilities, and substations; selective turf programs for municipal parks and sportsfields; and targeted treatment for Okanagan-specific invasive species including knapweed, sulphur cinquefoil, and burdock. Our drainage ditch programs control aquatic vegetation where needed.</p>
            </div>
          </AnimateOnScroll>

          {/* Other Service Areas */}
          <AnimateOnScroll animation="fade-in-up">
            <div className="text-container-blur">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 text-center">Other Regions We Serve</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Link to="/service-areas/lower-mainland" className="block bg-white/80 rounded-lg p-4 text-center hover-lift border border-gray-200">
                  <svg className="w-6 h-6 text-green-700 mx-auto mb-2" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" /><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" /></svg>
                  <h3 className="font-bold text-gray-900">Lower Mainland</h3>
                  <p className="text-gray-700 text-sm mt-1">Vancouver, Surrey & Fraser Valley</p>
                </Link>
                <Link to="/service-areas/interior-bc" className="block bg-white/80 rounded-lg p-4 text-center hover-lift border border-gray-200">
                  <svg className="w-6 h-6 text-green-700 mx-auto mb-2" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" /><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" /></svg>
                  <h3 className="font-bold text-gray-900">Interior BC</h3>
                  <p className="text-gray-700 text-sm mt-1">Kamloops, Merritt & Revelstoke</p>
                </Link>
                <Link to="/service-areas/northern-bc" className="block bg-white/80 rounded-lg p-4 text-center hover-lift border border-gray-200">
                  <svg className="w-6 h-6 text-green-700 mx-auto mb-2" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" /><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" /></svg>
                  <h3 className="font-bold text-gray-900">Northern BC</h3>
                  <p className="text-gray-700 text-sm mt-1">Prince George & Central Interior</p>
                </Link>
              </div>
            </div>
          </AnimateOnScroll>

          <AnimateOnScroll animation="fade-in-up">
            <div className="text-container-blur text-center">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">Get a Quote</h2>
              <p className="text-gray-700 mb-5">Your local vegetation management team. <Link to="/services" className="text-green-700 underline hover:text-green-900">View all services</Link> or contact us for a site assessment.</p>
              <Link to="/contact" className="cta-button">
                Get a Quote
              </Link>
            </div>
          </AnimateOnScroll>
        </div>
      </div>
    </div>
  );
};

export default OkanaganPage;
